/**
 * Countdown timer component for PIM sessions per BL-010.
 * Uses requestAnimationFrame for smooth updates.
 * Colour transitions at 10min (warning) and 5min (danger + pulse).
 */

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface CountdownTimerProps {
  readonly expiresAt: string;
  readonly className?: string;
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return "00:00:00";

  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export function CountdownTimer({ expiresAt, className }: CountdownTimerProps) {
  const [remaining, setRemaining] = useState(() => {
    const ms = new Date(expiresAt).getTime() - Date.now();
    return Math.max(0, ms);
  });
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const expiryMs = new Date(expiresAt).getTime();

    const tick = () => {
      const ms = Math.max(0, expiryMs - Date.now());
      setRemaining(ms);

      if (ms > 0) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
    };
  }, [expiresAt]);

  const minutes = remaining / 60_000;
  const isWarning = minutes <= 10 && minutes > 5;
  const isDanger = minutes <= 5;
  const isExpired = remaining <= 0;

  return (
    <span
      className={cn(
        "font-mono text-body-sm font-medium tabular-nums",
        isExpired && "text-sc-text-muted",
        isWarning && "text-amber-600 dark:text-amber-400",
        isDanger && "text-sc-danger animate-sc-pulse",
        !isExpired && !isWarning && !isDanger && "text-sc-text",
        className,
      )}
      aria-label={
        isExpired
          ? "Session expired"
          : `${formatRemaining(remaining)} remaining`
      }
    >
      {isExpired ? "Expired" : formatRemaining(remaining)}
    </span>
  );
}
